<template>
  <div class="page-shell privacy-page">
    <top-bar title="Política de Privacidade"/>
    <div class="container page-content">
      <div class="privacy-intro mb-4">
        <h1>Política de Privacidade do LeCard Entregador</h1>
        <p class="text-muted mb-0">Última atualização: 1º de setembro de 2026</p>
      </div>

      <section>
        <h2>1. Sobre esta política</h2>
        <p>Esta Política de Privacidade explica como o <strong>LeCard Entregador</strong>, disponibilizado pela LeCard, acessa, coleta, utiliza, armazena e compartilha informações durante o uso do aplicativo.</p>
        <p>O aplicativo é destinado a entregadores vinculados a empresas e restaurantes que utilizam a plataforma LeCard para organizar e acompanhar suas operações de entrega.</p>
      </section>

      <section>
        <h2>2. Dados tratados</h2>
        <p>O aplicativo pode tratar as seguintes informações:</p>
        <ul>
          <li><strong>Dados de acesso e identificação:</strong> nome, empresa vinculada, e-mail ou usuário, telefone utilizado na autenticação e tokens de sessão.</li>
          <li><strong>Localização exata:</strong> latitude, longitude, precisão, altitude, velocidade, direção, horário da captura, provedor da localização e indicação de localização simulada.</li>
          <li><strong>Dados do dispositivo:</strong> nível e estado de carregamento da bateria, sistema operacional e informações técnicas necessárias ao funcionamento e diagnóstico do aplicativo.</li>
          <li><strong>Dados operacionais:</strong> pedidos, rotas, situação das entregas, disponibilidade do entregador e registros de envio da localização.</li>
          <li><strong>Dados de diagnóstico:</strong> erros, falhas, desempenho e informações técnicas coletadas para manter a estabilidade e a segurança do aplicativo.</li>
        </ul>
      </section>

      <section>
        <h2>3. Uso da localização</h2>
        <p>A localização exata é necessária para que a empresa acompanhe a disponibilidade do entregador, organize as rotas, distribua novas entregas e acompanhe o andamento e o retorno das rotas.</p>
        <p>No aplicativo Android, a localização continua sendo coletada e compartilhada enquanto o entregador estiver conectado e disponível, inclusive ao usar outro aplicativo ou bloquear a tela. Durante esse período, o Android exibe uma notificação permanente.</p>
        <p>O compartilhamento termina ao sair da conta, encerrar a disponibilidade ou remover o aplicativo da tela de recentes. Sem a permissão de localização exata, não é possível utilizar o LeCard Entregador.</p>
      </section>

      <section>
        <h2>4. Como utilizamos os dados</h2>
        <p>As informações são utilizadas para:</p>
        <ul>
          <li>autenticar o entregador e manter sua sessão;</li>
          <li>exibir e atualizar pedidos e rotas;</li>
          <li>acompanhar a disponibilidade e a posição do entregador;</li>
          <li>organizar, distribuir e acompanhar entregas;</li>
          <li>enviar alertas e notificações operacionais;</li>
          <li>detectar falhas, prevenir abusos e melhorar a segurança e o desempenho do aplicativo;</li>
          <li>cumprir obrigações legais e proteger direitos da LeCard, das empresas vinculadas e dos usuários.</li>
        </ul>
      </section>

      <section>
        <h2>5. Compartilhamento</h2>
        <p>Os dados podem ser compartilhados somente conforme necessário com:</p>
        <ul>
          <li><strong>a empresa ou restaurante ao qual o entregador está vinculado</strong>, para gestão das entregas e acompanhamento operacional;</li>
          <li><strong>prestadores de infraestrutura e tecnologia</strong>, responsáveis por hospedagem, comunicação, armazenamento, monitoramento e suporte do aplicativo;</li>
          <li><strong>autoridades públicas</strong>, quando houver obrigação legal, regulatória ou ordem válida.</li>
        </ul>
        <p>A LeCard não vende dados pessoais nem utiliza a localização do entregador para publicidade.</p>
      </section>

      <section>
        <h2>6. Armazenamento e segurança</h2>
        <p>Parte das informações necessárias à sessão, preferências e funcionamento offline pode ser armazenada localmente no dispositivo. Dados operacionais são transmitidos aos servidores utilizados pela LeCard por conexões protegidas.</p>
        <p>São adotadas medidas técnicas e administrativas destinadas a proteger os dados contra acesso, alteração, divulgação ou destruição não autorizados. Nenhum sistema, entretanto, é completamente imune a incidentes.</p>
      </section>

      <section>
        <h2>7. Retenção e exclusão</h2>
        <p>Os dados são mantidos pelo período necessário para prestar o serviço, atender às finalidades descritas nesta política e cumprir obrigações legais, contratuais, de segurança e de auditoria.</p>
        <p>O logout remove do dispositivo os dados locais de sessão utilizados pelo aplicativo. Registros mantidos nos servidores podem permanecer pelos prazos aplicáveis à operação da empresa vinculada e às obrigações legais.</p>
        <p>Solicitações de acesso, correção ou exclusão de dados devem ser encaminhadas à empresa responsável pelo vínculo do entregador ou ao suporte da LeCard.</p>
      </section>

      <section>
        <h2>8. Permissões do dispositivo</h2>
        <p>O aplicativo solicita localização exata, notificações e execução de serviço de localização em primeiro plano. As permissões podem ser consultadas nas configurações do dispositivo, mas a revogação da localização impede o uso das funções principais do aplicativo.</p>
      </section>

      <section>
        <h2>9. Serviços de terceiros</h2>
        <p>O aplicativo utiliza serviços tecnológicos de terceiros, incluindo o Sentry para diagnóstico de erros e estabilidade. Esses prestadores tratam informações de acordo com suas obrigações contratuais e políticas de privacidade, limitados às finalidades necessárias à prestação dos serviços.</p>
      </section>

      <section>
        <h2>10. Crianças e adolescentes</h2>
        <p>O LeCard Entregador é uma ferramenta profissional e não é direcionado a crianças.</p>
      </section>

      <section>
        <h2>11. Alterações nesta política</h2>
        <p>Esta política poderá ser atualizada para refletir mudanças no aplicativo, nos serviços ou na legislação. A data da revisão mais recente será indicada no início deste documento.</p>
      </section>

      <section>
        <h2>12. Contato</h2>
        <p>Para dúvidas ou solicitações relacionadas à privacidade e ao tratamento de dados, entre em contato com a empresa à qual você está vinculado ou acesse o <a href="https://lecard.tawk.help" class="text-link" target="_blank" rel="noopener noreferrer">suporte oficial da LeCard</a>.</p>
      </section>
    </div>
  </div>
</template>

<script>
import TopBar from '@/components/TopBar';

export default {
  name: 'PoliticaPrivacidade',
  components: { TopBar }
}
</script>

<style scoped>
.privacy-page .page-content {
  padding-bottom: 64px;
}

.privacy-intro h1 {
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.35;
}

section {
  margin-bottom: 28px;
}

section h2 {
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 12px;
}

section p,
section li {
  line-height: 1.65;
}

section ul {
  padding-left: 22px;
}

section li + li {
  margin-top: 8px;
}
</style>
